import {
  NavigationModal,
  DatePicker,
  TextArea,
  FormItem,
  Form
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { Form as AntdForm } from 'antd'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import dayjs, { Dayjs } from 'dayjs'
import RotaWarningBox from './components/RotaWarningBox'
import { DeathDetails } from 'types/deathDetails'

type DeathDetailsModalProps = {
  showModal: boolean
  onCancel: () => void
  onConfirm: (deathDetails: DeathDetails) => void
  backNavigation: () => void
  name: string
  initialValues?: DeathDetails
  isLoading?: boolean
  showRotaWarning?: boolean
}

type DeathDetailsFormValues = {
  deathDetails?: string
  deathDate?: Dayjs
}

const DeathDetailsModal = ({
  showModal,
  onCancel,
  onConfirm,
  backNavigation,
  name,
  initialValues,
  isLoading,
  showRotaWarning = false
}: DeathDetailsModalProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm<DeathDetailsFormValues>()

  const handleConfirm = () => {
    form.validateFields().then((values) => {
      const deathDetails: DeathDetails = {
        deathDetails: values.deathDetails || null,
        deathDate: values.deathDate?.format('YYYY-MM-DD') || null
      }
      onConfirm(deathDetails)
    })
  }

  const getInitialValues = (): DeathDetailsFormValues => {
    return {
      deathDetails: initialValues?.deathDetails || undefined,
      deathDate: initialValues?.deathDate
        ? dayjs(initialValues.deathDate)
        : undefined
    }
  }

  return (
    <NavigationModal
      modalTitle={t(supConstants.DEATH_DETAILS_TITLE)}
      modalConfirmText={t(supConstants.SAVE)}
      modalCancelText={t(supConstants.CANCEL)}
      showPrompt={showModal}
      confirmNavigation={handleConfirm}
      cancelNavigation={onCancel}
      confirmButtonProps={{ variant: 'primary', isLoading }}
      hasBackButton
      backButtonText={t(supConstants.BACK)}
      backNavigation={backNavigation}
    >
      <Container>
        <Form form={form} layout="vertical" initialValues={getInitialValues()}>
          <FormItem
            label={<Text>{t(supConstants.DETAILS_OF_DEATH)}</Text>}
            name="deathDetails"
          >
            <TextArea
              placeholder={t(supConstants.DETAILS_OF_DEATH)}
              rows={3}
              data-testid="death-details-textarea"
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.DATE_OF_DEATH)}</Text>}
            name="deathDate"
          >
            <StyledDatePicker
              format="DD-MM-YYYY"
              data-testid="death-details-date-picker"
            />
          </FormItem>
        </Form>
        {showRotaWarning && (
          <RotaWarningBox
            name={name}
            status="deceased"
            confirmButtonText={t(supConstants.SAVE)}
          />
        )}
      </Container>
    </NavigationModal>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 300px;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

export default DeathDetailsModal
